import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { DialogService } from '../../services/dialog.service';
import { DataSourceGet, DataSourceUpdate } from '../../models/datasource';
import { EditFileUploadDatasourceComponent } from './edit-file-upload-datasource/edit-file-upload-datasource.component';
import { EditApiListenerDatasourceComponent } from './edit-api-listener-datasource/edit-api-listener-datasource.component';

export interface EditDataSourceDialogData {
  dataSource: DataSourceGet;
}

export interface EditDataSourceResult {
  updated: boolean;
  deleted?: boolean;
}

@Component({
  selector: 'app-edit-datasource-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    FormsModule,
    EditFileUploadDatasourceComponent,
    EditApiListenerDatasourceComponent
  ],
  templateUrl: './edit-datasource-dialog.component.html',
  styleUrls: ['./edit-datasource-dialog.component.scss']
})
export class EditDataSourceDialogComponent implements OnInit {
  @ViewChild(EditFileUploadDatasourceComponent) fileUploadComponent?: EditFileUploadDatasourceComponent;

  dataSource: DataSourceGet;
  dataSourceName: string = '';
  tags: string = '';
  loading = false;
  loadingData = true; // Loading state for initial data fetch
  error: string | null = null;
  
  // Datasource types
  readonly UPLOAD_FILE_TYPE = 3;
  readonly API_LISTENER_TYPE = 1;

  constructor(
    public dialogRef: MatDialogRef<EditDataSourceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditDataSourceDialogData,
    private dataService: DataService,
    private dialogService: DialogService
  ) {
    // Initialize with provided data as a fallback
    this.dataSource = data.dataSource;
    this.dataSourceName = data.dataSource.dataSourceName;
    this.tags = data.dataSource.tags || '';
  }

  ngOnInit(): void {
    // Fetch fresh datasource data from the API to ensure we have all details
    // (list endpoints may not include all fields like endpoint in dataSourceAccessJson)
    if (this.dataSource.dataSourceId) {
      this.loadingData = true;
      this.dataService.getDataSource(this.dataSource.dataSourceId).subscribe({
        next: (freshDataSource) => {
          this.dataSource = freshDataSource;
          this.dataSourceName = freshDataSource.dataSourceName;
          this.tags = freshDataSource.tags || '';
          this.loadingData = false;
        },
        error: (error) => {
          // If fetch fails, continue with the provided data
          console.error('Error fetching fresh datasource data:', error);
          this.error = 'Could not load complete data source details. Some information may be missing.';
          this.loadingData = false;
        }
      });
    } else {
      this.loadingData = false;
    }
  }

  isUploadFileType(): boolean {
    return this.dataSource.dataSourceTypeId === this.UPLOAD_FILE_TYPE;
  }

  isApiListenerType(): boolean {
    return this.dataSource.dataSourceTypeId === this.API_LISTENER_TYPE;
  }

  onSave(): void {
    if (!this.dataSourceName.trim()) {
      this.error = 'Data source name is required';
      return;
    }

    this.loading = true;
    this.error = null;

    const updateData: DataSourceUpdate = {
      dataSourceId: this.dataSource.dataSourceId,
      dataSourceTypeId: this.dataSource.dataSourceTypeId,
      dataSourceName: this.dataSourceName.trim(),
      dataSourceAccessJson: this.dataSource.dataSourceAccessJson,
      tags: this.tags.trim() || undefined
    };

    this.dataService.updateDataSource(updateData).subscribe({
      next: (success) => {
        if (success) {
          if (this.fileUploadComponent?.selectedFile) {
            this.fileUploadComponent.performReplace().subscribe({
              next: () => {
                this.loading = false;
                this.dialogRef.close({ updated: true } as EditDataSourceResult);
              },
              error: (error) => {
                this.loading = false;
                this.error = error.message || 'Failed to replace file';
              }
            });
          } else {
            this.loading = false;
            this.dialogRef.close({ updated: true } as EditDataSourceResult);
          }
        } else {
          this.loading = false;
          this.error = 'Failed to update data source';
        }
      },
      error: (error) => {
        this.loading = false;
        this.error = error.message || 'Failed to update data source';
        console.error('Error updating data source:', error);
      }
    });
  }

  onDelete(): void {
    this.dialogService.openConfirmDialog(
      'Delete Data Source',
      `Are you sure you want to delete "${this.dataSource.dataSourceName}"? This action cannot be undone and will affect all projects using this data source.`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed && this.dataSource.dataSourceId) {
        this.loading = true;
        this.dataService.deleteDataSource(this.dataSource.dataSourceId).subscribe({
          next: (success) => {
            this.loading = false;
            if (success) {
              this.dialogRef.close({ updated: true, deleted: true } as EditDataSourceResult);
            } else {
              this.error = 'Failed to delete data source';
            }
          },
          error: (error) => {
            this.loading = false;
            this.error = error.message || 'Failed to delete data source';
            console.error('Error deleting data source:', error);
          }
        });
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
